import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapStyleSelector from '../components/MapStyleSelector';
import FireToggle from '../components/FireToggle';
import FireLayer from '../components/FireLayer';
import { DEFAULT_MAP_STYLE, getMapStyleByKey } from '../constants/mapStyles';
import './IRMap.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create a custom icon with label for response units
const createLabeledIcon = (label) => {
  return L.divIcon({
    className: 'custom-labeled-icon',
    html: `
      <div class="marker-with-label">
        <img src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png" class="marker-icon" />
        <div class="marker-label">${label}</div>
      </div>
    `,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });
};

function IRMap() {
  const [mapStyleKey, setMapStyleKey] = useState(DEFAULT_MAP_STYLE.key);
  const [showFires, setShowFires] = useState(false);
  const activeMapStyle = getMapStyleByKey(mapStyleKey);

  // Mock properties data - in the future this would come from a service/context
  const [properties] = useState([
    {
      id: 1,
      name: 'Bar H Ranch',
      location: '1632 E HWY 92',
      coordinates: { lat: 47.0527, lng: -109.6333 },
      size: '2,500 acres',
      livestock: ['Cattle', 'Sheep'],
      manager: 'John Smith',
      contact: '(406) 555-0123',
    },
    {
      id: 2,
      name: 'Prairie View Farm',
      location: '1414 CO Rd 110',
      coordinates: { lat: 43.0759, lng: -107.2903 },
      size: '1,800 acres',
      livestock: ['Cattle', 'Horses'],
      manager: 'You',
      contact: '(307) 555-0456',
    }
  ]);

  // Additional pins for incident response units
  const [responsePins] = useState([
    {
      id: 'unit1',
      name: 'Hackbart 001',
      coordinates: { lat: 47.1234, lng: -109.5678 },
      type: 'unit'
    },
    {
      id: 'unit2',
      name: 'Hackbart 007',
      coordinates: { lat: 46.9876, lng: -109.7123 },
      type: 'unit'
    },
    {
      id: 'unit3',
      name: 'SVFD #3',
      coordinates: { lat: 47.0123, lng: -109.4567 },
      type: 'unit'
    },
    {
      id: 'unit4',
      name: 'SVFD #9',
      coordinates: { lat: 47.1567, lng: -109.7890 },
      type: 'unit'
    },
    {
      id: 'unit5',
      name: 'Sheriff #2',
      coordinates: { lat: 46.9234, lng: -109.5432 },
      type: 'unit'
    }
  ]);

  // Mock chat messages
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'SVFD #3',
      text: 'We have eyes on the fire. Approximately 50 acres burning on the north side.',
      timestamp: new Date(Date.now() - 15 * 60000) // 15 minutes ago
    },
    {
      id: 2,
      sender: 'Hackbart 001',
      text: 'What is the best access route to the north pasture?',
      timestamp: new Date(Date.now() - 12 * 60000) // 12 minutes ago
    },
    {
      id: 3,
      sender: 'Sheriff #2',
      text: 'Road is blocked at mile marker 14. Recommend alternate route via County Road 110.',
      timestamp: new Date(Date.now() - 10 * 60000) // 10 minutes ago
    },
    {
      id: 4,
      sender: 'SVFD #9',
      text: 'Setting up water supply at the creek. ETA 5 minutes.',
      timestamp: new Date(Date.now() - 8 * 60000) // 8 minutes ago
    },
    {
      id: 5,
      sender: 'Hackbart 007',
      text: 'Wind is shifting to the east. Fire spreading toward livestock area.',
      timestamp: new Date(Date.now() - 5 * 60000) // 5 minutes ago
    },
    {
      id: 6,
      sender: 'SVFD #3',
      text: 'Copy that. We need to evacuate the cattle immediately.',
      timestamp: new Date(Date.now() - 3 * 60000) // 3 minutes ago
    },
    {
      id: 7,
      sender: 'Hackbart 001',
      text: 'Arriving at north gate now. Opening access for fire crews.',
      timestamp: new Date(Date.now() - 1 * 60000) // 1 minute ago
    }
  ]);

  const [newMessage, setNewMessage] = useState('');

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (newMessage.trim()) {
      const message = {
        id: messages.length + 1,
        sender: 'Command Center',
        text: newMessage,
        timestamp: new Date()
      };
      setMessages([...messages, message]);
      setNewMessage('');
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Center on Sunset Ranch
  const centerProperty = properties[0];
  const mapCenter = [centerProperty.coordinates.lat, centerProperty.coordinates.lng];

  return (
    <div className="irmap-page">
      <div className="irmap-header">
        <h1>Incident Response Map</h1>
        <p className="irmap-subtitle">Centered on: {centerProperty.name}</p>
      </div>

      <div className="irmap-main-content">
        <div className="irmap-container">
          <MapStyleSelector active={mapStyleKey} onChange={setMapStyleKey} />
          <FireToggle active={showFires} onChange={setShowFires} />
          <MapContainer
            center={mapCenter}
            zoom={9}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              key={activeMapStyle.key}
              url={activeMapStyle.url}
              attribution={activeMapStyle.attribution}
            />
            {properties.map((property) => (
              <Marker
                key={property.id}
                position={[property.coordinates.lat, property.coordinates.lng]}
              >
                <Popup>
                  <div className="property-popup">
                    <h3>{property.name}</h3>
                    <p><strong>Location:</strong> {property.location}</p>
                    <p><strong>Size:</strong> {property.size}</p>
                    <p><strong>Manager:</strong> {property.manager}</p>
                    <p><strong>Contact:</strong> {property.contact}</p>
                    <p><strong>Livestock:</strong> {property.livestock.join(', ')}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
            {responsePins.map((pin) => (
              <Marker
                key={pin.id}
                position={[pin.coordinates.lat, pin.coordinates.lng]}
                icon={createLabeledIcon(pin.name)}
              >
                <Popup>
                  <div className="property-popup">
                    <h3>{pin.name}</h3>
                    <p><strong>Type:</strong> Response Unit</p>
                  </div>
                </Popup>
              </Marker>
            ))}
            <FireLayer center={{ lat: mapCenter[0], lng: mapCenter[1] }} visible={showFires} />
          </MapContainer>
        </div>

        <div className="chat-container">
          <div className="chat-header">
            <h3>Group Chat</h3>
            <span className="chat-participants">{responsePins.length} units online</span>
          </div>

          <div className="chat-messages">
            {messages.map((message) => (
              <div key={message.id} className="chat-message">
                <div className="message-header">
                  <span className="message-sender">{message.sender}</span>
                  <span className="message-time">{formatTime(message.timestamp)}</span>
                </div>
                <div className="message-text">{message.text}</div>
              </div>
            ))}
          </div>

          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <input
              type="text"
              className="chat-input"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <button type="submit" className="chat-send-button">Send</button>
          </form>
        </div>
      </div>

      <div className="irmap-controls">
        <button className="button secondary-button">Button 1</button>
        <button className="button secondary-button">Button 2</button>
        <button className="button secondary-button">Button 3</button>
        <button className="button primary-button">Button 4</button>
      </div>
    </div>
  );
}

export default IRMap;

